import { Alert, Button, TextInput, TextLink } from '@mgmresorts/mgm-ui';
import React, { useContext, useMemo, useEffect } from 'react';

import { Container } from '../../components/ui/Container';
import { Logo } from '../../components/ui/Logo';
import { PageLayout } from '../../components/ui/PageLayout';
import { useContent } from '../../hooks/useContent';
import { useFieldValidation } from '../../hooks/useFieldValidation';
import { useInput } from '../../hooks/useInput';
import { useLoadingLabel } from '../../hooks/useLoadingLabel';
import { useEvent } from '../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../utils/analytics/usePageLoadEvent';
import { activationCodeValidator } from '../../utils/validators';

import { SignUpFormContext } from './SignUpFormContext';
import {
  StyledAlertContainer,
  StyledContent,
  StyledHeading,
  StyledInfo,
  StyledVerificationCodeContainer,
} from './styles';

export function SignUpVerification() {
  usePageLoadEvent('sign up', 'email verification');
  const { signUpCodeVerificationError, verifySignupCode, email, backToSignUp, isLoading } =
    useContext(SignUpFormContext);
  const { validateField, invalidFields } = useFieldValidation([activationCodeValidator]);
  const { bind: bindActivationCode, value: activationCode } = useInput('', 'activationCode', undefined, validateField);
  const { getContentByPath } = useContent();
  const trackEvent = useEvent();
  const verifyEmailCTA = useLoadingLabel(isLoading, 'signUpVerification.cta');
  useEffect(() => {
    trackEvent('identity – email verification - start', 'start email verification process', 'emailverificationstart');
  }, [trackEvent]);

  const errorMessage = useMemo<string>(
    () =>
      invalidFields.includes('activationCode')
        ? !activationCode || activationCode.length === 0
          ? getContentByPath(`signUpVerification.fields.activationCode.required`)
          : getContentByPath(`signUpVerification.fields.activationCode.error`)
        : '',
    [activationCode, getContentByPath, invalidFields],
  );

  return (
    <StyledContent>
      <PageLayout
        title={getContentByPath('signUpVerification.title')}
        description={getContentByPath('signUpVerification.description')}
      >
        <Container height='auto'>
          <Logo />

          <StyledHeading variant='heading-medium-3' as='h1' className='identity__page-heading'>
            {getContentByPath('signUpVerification.title')}
          </StyledHeading>

          {!isLoading && signUpCodeVerificationError && (
            <StyledAlertContainer>
              <Alert open variant='error' message={signUpCodeVerificationError} hiddenCloseButton />
            </StyledAlertContainer>
          )}

          <StyledInfo
            variant='body-regular-m'
            dangerouslySetInnerHTML={{
              __html: (getContentByPath('signUpVerification.body') as string).replace('%email%', email ?? ''),
            }}
          />
          <StyledVerificationCodeContainer>
            <TextInput
              variant='default'
              label={getContentByPath('signUpVerification.fields.activationCode.title')}
              id='activationCode'
              testId='activationCode'
              disabled={false}
              className={`identity__field identity__field-activation-code ${
                invalidFields.includes('activationCode') ? 'with-error' : ''
              }`}
              error={!!errorMessage}
              hint={errorMessage}
              {...bindActivationCode}
              inputMode='numeric'
              autoComplete='one-time-code'
            />
          </StyledVerificationCodeContainer>

          {activationCode.length === 0 && (
            <StyledAlertContainer>
              <Alert
                open
                variant='informative'
                message={getContentByPath('signUpVerification.message')}
                hiddenCloseButton
                actions={
                  <TextLink button variant='small' onClick={backToSignUp}>
                    {getContentByPath('signUpVerification.resend')}
                  </TextLink>
                }
              />
            </StyledAlertContainer>
          )}

          <Button
            variant='primary'
            size='large'
            testId='activate-button'
            onClick={() => verifySignupCode(activationCode)}
            className='form-button identity__form-submit identity__signup-verify-form-submit'
            disabled={isLoading || !activationCode || invalidFields.includes('activationCode')}
            loading={isLoading}
            fullWidth
          >
            {verifyEmailCTA}
          </Button>
        </Container>
      </PageLayout>
    </StyledContent>
  );
}
